
import { useState, type ReactNode } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils/cn";

interface Tab {
  value: string;
  label: string;
  icon?: ReactNode;
  badge?: number;
  disabled?: boolean;
}

interface TabsProps {
  tabs: Tab[];
  defaultValue?: string;
  value?: string;
  onChange?: (value: string) => void;
  variant?: "line" | "pill" | "box";
  size?: "sm" | "md";
  className?: string;
  children?: (activeTab: string) => ReactNode;
}

export function Tabs({
  tabs, defaultValue, value: controlled, onChange,
  variant = "line", size = "md", className, children
}: TabsProps) {
  const [internal, setInternal] = useState(defaultValue || tabs[0]?.value || "");
  const active = controlled ?? internal;

  const handleChange = (val: string) => {
    setInternal(val);
    onChange?.(val);
  };

  const sizeMap = { sm: "text-xs py-1.5 px-3", md: "text-sm py-2 px-4" };
  const base = "relative flex items-center gap-1.5 font-medium transition-colors duration-150 whitespace-nowrap";

  return (
    <div className={className}>
      <div className={cn(
        "flex items-center",
        variant === "line" && "border-b border-border",
        variant === "pill" && "bg-surface-overlay rounded-2xl p-1 border border-border",
        variant === "box" && "border border-border rounded-xl p-1 bg-surface-overlay",
      )}>
        {tabs.map(tab => (
          <button
            key={tab.value}
            onClick={() => !tab.disabled && handleChange(tab.value)}
            disabled={tab.disabled}
            className={cn(
              base, sizeMap[size],
              variant === "line" && [
                "border-b-2 -mb-px",
                active === tab.value
                  ? "border-brand text-brand"
                  : "border-transparent text-text-muted hover:text-foreground"
              ],
              (variant === "pill" || variant === "box") && [
                "rounded-xl flex-1 justify-center",
                active === tab.value
                  ? "bg-surface-elevated text-foreground shadow-sm border border-border"
                  : "text-text-muted hover:text-foreground"
              ],
              tab.disabled && "opacity-40 cursor-not-allowed"
            )}
          >
            {tab.icon}
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 && (
              <span className={cn(
                "min-w-4 h-4 flex items-center justify-center rounded-full text-2xs font-bold px-1",
                active === tab.value ? "bg-brand/20 text-brand" : "bg-muted text-text-muted"
              )}>
                {tab.badge > 99 ? "99+" : tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>
      {children && <div className="mt-4">{children(active)}</div>}
    </div>
  );
}
